/**
 * Resolves image paths written in Markdown to their built, content-hashed URLs.
 *
 * Article bodies are loaded as raw strings, so Vite never sees an image path in
 * Markdown as an import and will not rewrite it. Without this map, `![x](…)`
 * would emit the literal source path and 404 in production.
 *
 * Globbing them separately gets the hashing back: each image is emitted to
 * dist/assets with a content hash, so a replaced screenshot gets a new URL and
 * no customer sees a cached copy of the old one. That matters here more than
 * most places — help screenshots go stale exactly when a third party changes
 * their UI, which is when everyone needs to see the new one.
 */
const modules = import.meta.glob(
  "../../content/help/images/**/*.{png,jpg,jpeg,webp,gif,svg}",
  { eager: true, import: "default" }
) as Record<string, string>;

const CONTENT_ROOT = "../../content/help/";

const byPath = new Map(
  Object.entries(modules).map(([path, url]) => [
    path.slice(CONTENT_ROOT.length),
    url,
  ])
);

/**
 * Paths are written relative to the content directory —
 * `![Webhook](images/traderspost-setup/webhook.png)`. Explicit rather than
 * inferred from the slug: it is greppable, and the build validator can check
 * the exact string an author typed.
 */
export function resolveHelpImage(src: string): string | null {
  if (/^(https?:)?\/\//.test(src) || src.startsWith("data:")) return src;
  return byPath.get(src.replace(/^\.\//, "")) ?? null;
}
